import clsx from 'clsx';
import React, { Children, FC, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { IconType } from 'react-icons';
import { FaCheck } from 'react-icons/fa';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { RadioButton } from '../RadioButton';

export interface DropdownOption {
  id: string;
  value: any;
  name?: string;
}

export enum GridDropdownType {
  DEFAULT = 'default',
  ICON = 'icon',
  COLOR = 'color'
}

export interface GridSelectTab {
  label: string;
  key: string;
}

export interface GridSelectTabs {
  headers: GridSelectTab[];
  activeHeader: string;
  onTabChange: (key: string) => void;
}

export interface GridSelectOptionProps {
  option: DropdownOption;
  className?: string;
  isSelected?: boolean;
  type?: GridDropdownType;
  onChange?: (option: DropdownOption) => void;
}

export const GridSelectOption: FC<GridSelectOptionProps> = ({
  option,
  className,
  isSelected,
  type = GridDropdownType.DEFAULT,
  onChange
}) => {
  const Icon = type === GridDropdownType.ICON ? (option?.value as IconType) : null;

  return (
    <div
      className={clsx(
        'w-8 h-8 rounded hover:cursor-pointer hover:bg-[#1bb55c20] flex items-center justify-center relative',
        className
      )}
      onClick={() => onChange?.(option)}
    >
      {isSelected && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-[52%] h-[52%] flex items-center justify-center rounded-full bg-[#ffffffcc]">
            <FaCheck className="w-full h-full text-[#1bb55ccc] border border-white rounded-full" />
          </div>
        </div>
      )}
      {type === GridDropdownType.ICON && Icon && <Icon className="w-5 h-5" />}
      {type === GridDropdownType.COLOR && (
        <div
          className="w-full h-full rounded-full shadow border border-[#32323240]"
          style={{ background: option?.value }}
        />
      )}
    </div>
  );
};

interface SelectedDisplayProps {
  type: GridDropdownType;
  selectedOption?: DropdownOption;
}

const SelectedDisplay: FC<SelectedDisplayProps> = ({ type, selectedOption }) => {
  if (type === GridDropdownType.ICON) {
    const Icon = selectedOption?.value as IconType | undefined;
    return Icon ? <Icon className="w-6 h-6" /> : null;
  }

  if (type === GridDropdownType.COLOR) {
    return (
      <div
        className="w-full h-full rounded-sm border border-[#32323240]"
        style={{ backgroundColor: selectedOption?.value }}
      />
    );
  }

  return <span className="text-xs">{selectedOption?.name}</span>;
};

interface GridSelectProps extends PropsWithChildren {
  className?: string;
  itemsPerRow: number;
  selectedId?: string | number;
  dropdownOptions?: DropdownOption[];
  readOnly?: boolean;
  type: GridDropdownType;
  tabs?: GridSelectTabs;
  onChange?: (id: string) => void;
}

export const GridSelect: FC<GridSelectProps> = ({
  className,
  children,
  itemsPerRow,
  selectedId,
  dropdownOptions = [],
  readOnly,
  type,
  tabs,
  onChange
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [opensUpward, setOpensUpward] = useState(false);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleToggle = () => {
    if (!isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setOpensUpward(window.innerHeight - rect.bottom < 200);
    }
    setIsOpen(prev => !prev);
  };

  const selectedOption = dropdownOptions.find(o => o.id === selectedId);

  const enhancedChildren = Children.map(children, child => {
    if (!React.isValidElement<GridSelectOptionProps>(child)) return child;

    return React.cloneElement(child, {
      isSelected: selectedId === child.props.option?.id,
      onChange: (opt: DropdownOption) => {
        child.props.onChange?.(opt);
        onChange?.(opt.id);
        setIsOpen(false);
      }
    });
  });

  if (readOnly) {
    if (type === GridDropdownType.COLOR) {
      return (
        <div
          className="h-6 w-16 rounded-md border border-[#32323240]"
          style={{ backgroundColor: selectedOption?.value }}
        />
      );
    }
    return <span className="text-sm">{selectedOption?.name}</span>;
  }

  return (
    <div ref={containerRef} className={clsx('select-none relative h-11', className)}>
      <div
        className={clsx(
          'flex items-center justify-between border rounded-md h-full hover:cursor-pointer',
          isOpen && 'outline outline-[#0070D2] outline-2',
          type === GridDropdownType.COLOR ? 'p-2 gap-1' : 'px-2 py-1'
        )}
        onClick={handleToggle}
      >
        <div
          className={clsx(
            'flex items-center overflow-hidden flex-1 h-full',
            type === GridDropdownType.ICON && 'justify-center'
          )}
        >
          <SelectedDisplay type={type} selectedOption={selectedOption} />
        </div>
        {isOpen ? <FiChevronUp className="shrink-0" /> : <FiChevronDown className="shrink-0" />}
      </div>

      {isOpen && (
        <div
          className={clsx(
            'absolute z-50 w-max max-w-screen-md flex flex-col gap-2',
            'border border-[#32323240] rounded-lg bg-[#fafafa] p-2',
            type === GridDropdownType.ICON && 'max-h-[200px]',
            opensUpward ? 'bottom-full mb-1' : 'top-full mt-1'
          )}
        >
          {tabs && (
            <div className="flex gap-3">
              {tabs.headers.map(header => (
                <div key={header.key} className="flex items-center gap-1">
                  <RadioButton
                    checked={header.key === tabs.activeHeader}
                    onClick={() => tabs.onTabChange(header.key)}
                  />
                  <p className="text-sm font-medium">{header.label}</p>
                </div>
              ))}
            </div>
          )}
          <div className="overflow-y-auto">
            <div className="grid" style={{ gridTemplateColumns: `repeat(${itemsPerRow}, 1fr)` }}>
              {enhancedChildren}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
